<template>
  <!-- Selector de impresora Zebra (Browser Print): la elección se guarda en
       ESTE equipo (localStorage) — la impresora está enchufada a esta máquina.
       "Predeterminada" = la que Browser Print tenga configurada por defecto. -->
  <div class="zebra-printer-select d-flex align-center flex-wrap ga-2">
    <v-select
      v-model="selectedKey"
      :items="items"
      item-title="title"
      item-value="key"
      label="Impresora Zebra (Browser Print)"
      density="compact"
      variant="outlined"
      hide-details
      :loading="loading"
      :disabled="loading || !items.length"
      prepend-inner-icon="mdi-printer-wireless"
      class="flex-grow-1"
      style="min-width: 260px; max-width: 460px"
      @update:model-value="onSelect"
    ></v-select>
    <v-btn icon="mdi-refresh" size="small" variant="text" :loading="loading"
           title="Volver a buscar impresoras conectadas" @click="load"></v-btn>
    <v-chip v-if="effectiveProfile && effectiveProfile.id === 'legacy'" size="small" color="orange" variant="tonal"
            title="Firmware ZPL clásico (V45): no admite UTF-8, los textos se imprimen sin tildes; DataMatrix con escape ~">
      {{ effectiveProfile.label }}
    </v-chip>
    <span v-if="error" class="text-caption text-error">{{ error }}</span>
    <span v-else-if="missingSaved" class="text-caption text-warning">
      La impresora elegida no está conectada — elige otra o "Predeterminada"
    </span>
  </div>
</template>

<script>
import { listBrowserPrintPrinters, getSavedPrinter, savePrinter, printerSerial } from '@/utils/browserPrint'
import { profileForPrinter } from '@/utils/zplLabel'

const DEFAULT_KEY = '__default__'

export default {
  name: 'ZebraPrinterSelect',
  data() {
    return {
      loading: false,
      error: '',
      printers: [],
      defaultDevice: null,
      saved: getSavedPrinter(),
      selectedKey: DEFAULT_KEY
    }
  },
  computed: {
    items() {
      const list = []
      const def = this.defaultDevice
      list.push({
        key: DEFAULT_KEY,
        title: def ? `Predeterminada de Browser Print: ${def.name}` : 'Predeterminada de Browser Print'
      })
      for (const p of this.printers) {
        list.push({ key: this.keyOf(p), title: `${p.name} — ${p.connection === 'usb' ? 'USB' : p.connection}` })
      }
      if (this.saved && this.missingSaved) {
        list.push({ key: this.keyOf(this.saved), title: `${this.saved.name} (no conectada)` })
      }
      return list
    },
    // La impresora guardada no aparece entre las conectadas
    missingSaved() {
      return !!this.saved && !this.printers.some(p => this.keyOf(p) === this.keyOf(this.saved))
    },
    effectiveDevice() {
      if (this.selectedKey === DEFAULT_KEY) return this.defaultDevice
      return this.printers.find(p => this.keyOf(p) === this.selectedKey) || this.saved
    },
    effectiveProfile() {
      return this.effectiveDevice ? profileForPrinter(this.effectiveDevice) : null
    }
  },
  mounted() {
    this.load()
  },
  methods: {
    // Clave estable del equipo físico: número de serie (sobrevive a los
    // cambios de dirección USB); si no hay serie, nombre+conexión
    keyOf(p) {
      return printerSerial(p) || `${p.name}|${p.connection}`
    },
    async load() {
      this.loading = true
      this.error = ''
      try {
        const svc = await listBrowserPrintPrinters()
        if (!svc) {
          this.printers = []
          this.defaultDevice = null
          this.error = 'Zebra Browser Print no responde (¿está corriendo y autorizado este sitio?)'
        } else {
          this.printers = svc.printers
          this.defaultDevice = svc.defaultDevice
        }
        this.saved = getSavedPrinter()
        this.selectedKey = this.saved ? this.keyOf(this.saved) : DEFAULT_KEY
      } finally {
        this.loading = false
      }
    },
    onSelect(key) {
      if (key === DEFAULT_KEY) {
        savePrinter(null)
        this.saved = null
        return
      }
      const p = this.printers.find(x => this.keyOf(x) === key)
      if (p) {
        savePrinter(p)
        this.saved = p
      }
    }
  }
}
</script>
